import { flattenDeep, isEqual, isNull, isUndefined, set } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { deleteFewShotDocument, fetchAllFewShotDocuments, getFewShotDocumentById, getFewShotDocumentPriorityOrder, openFewShotDocument, updateFewShotDocumentAnswers, updateFewShotDocumentPriorityOrder, uploadFewShotDocuments } from '../../../../../services/fewShotDocument';
import { getDocumentUrl } from '../../../../../utils/getDocumentUrl';
import { MLData } from '../../../../datasets/store/mlTypes';
import { setIsCheckingHighlightIndexes } from '../../../../documents/document-viewer/store/actions';
import { getDocumentViewerType, getPdfText } from '../../../../documents/document-viewer/store/selectors';
import { DocumentViewerType, PdfActionTypes, PdfPageText } from '../../../../documents/document-viewer/store/types';
import { getHighlightIndexesForPageText } from '../../../../documents/document-viewer/store/utils';
import { ColumnSort, TableFilters } from '../../../../shared/table/types';
import { deleteFewShotDocumentFailed, deleteFewShotDocumentSuccessful, fetchFewShotDocumentsFailed, fetchFewShotDocumentsStarted, fetchFewShotDocumentsSuccessful, openFewShotDocumentAndQueries, openFewShotDocumentFailed, openFewShotDocumentStarted, openFewShotDocumentSuccessful, setAIAnswerHighlightIndexes, setInitialDocuments, setSelectedAgreementTypeId, updateFewShotAnswersFailed, updateFewShotAnswersSuccessful, updateFewShotPriorityOrder, updateFewShotPriorityOrderFailed, updateFewShotPriorityOrderSuccessful, uploadFewShotDocumentsFailed, uploadFewShotDocumentsSuccessful } from './actions';
import { getCurrentPriorityOrder, getDocumentToDelete, getFewShotAnswers, getFewShotDocument, getFewShotDocuments, getFewShotDocumentsTotal, getFilesToUpload, getFileUploadInformation, getPriorityOrderHasUpdated, getSavedFewShotDocument, getSelectedAgreementTypeId, getTableColumnSort, getTableFilters, getTablePageNumber, getTablePageSize } from './selectors';
import { DocumentNameFewShotPriorityOrder, FewShotDocumentActionTypes, FewShotDocumentQuery, FewShotDocumentTable, FewShotDocumentUploadInfo, MLFewShotDocument } from './types';
import { getSelectedClientId } from '../../../clients/store/selectors';

export function* attemptFetchAgreementTypeDocuments({ payload }: ReturnType<typeof setSelectedAgreementTypeId>) {
    const { agreementTypeId, shouldRedirect } = payload;
    yield put(fetchFewShotDocumentsStarted());
    if (shouldRedirect) {
        const navigationPath = isNull(agreementTypeId) ? '/system/ai/few-shot' : `/system/ai/few-shot/${agreementTypeId}`;
        yield put(push(navigationPath));
    }
    if (!isNull(agreementTypeId)) {
        const priorityOrder: DocumentNameFewShotPriorityOrder = yield call(getFewShotDocumentPriorityOrder, { agreementTypeId });
        const setInitial = true;
        yield put(updateFewShotPriorityOrder(priorityOrder, setInitial));
    }
}

function* selectedAgreementTypeUpdatedWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.SET_SELECTED_FEW_SHOT_AGREEMENT_TYPE, attemptFetchAgreementTypeDocuments);
}

export function* attemptFetchFewShotDocuments({ payload }: ReturnType<typeof fetchFewShotDocumentsStarted>) {
    try {
        const pageNumber = payload;
        const agreementTypeId: number | null = yield select(getSelectedAgreementTypeId);
        if (!isNull(agreementTypeId)) {
            const filters: TableFilters = yield select(getTableFilters);
            const columnSort: ColumnSort | undefined = yield select(getTableColumnSort);
            const pageSize: number = yield select(getTablePageSize);
            const { documents, total }: { documents: FewShotDocumentTable[]; total: number; } = yield call(fetchAllFewShotDocuments, { pageNumber, pageSize, filters, columnSort, agreementTypeId });
            yield put(fetchFewShotDocumentsSuccessful(documents, total));
        }
    } catch (e) {
        yield put(fetchFewShotDocumentsFailed((e as Error).message));
    }
}

function* fetchFewShotDocumentsWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.FETCH_AGREEMENT_TYPE_FEW_SHOT_DOCUMENTS_STARTED, attemptFetchFewShotDocuments);
}

export function* attemptPaginationNext() {
    const pageNumber: number = yield select(getTablePageNumber);
    yield put(fetchFewShotDocumentsStarted(pageNumber + 1));
}

function* paginationNextWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.FEW_SHOT_DOCUMENTS_PAGINATION_NEXT, attemptPaginationNext);
}

export function* attemptPaginationPrevious() {
    const pageNumber: number = yield select(getTablePageNumber);
    yield put(fetchFewShotDocumentsStarted(pageNumber - 1));
}

function* paginationPreviousWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.FEW_SHOT_DOCUMENTS_PAGINATION_PREVIOUS, attemptPaginationPrevious);
}

export function* tableConfigUpdated() {
    yield put(fetchFewShotDocumentsStarted(1));
}

function* tableConfigUpdatedWatcher() {
    yield debounce(200, [FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_PAGE_SIZE, FewShotDocumentActionTypes.CLEAR_FEW_SHOT_DOCUMENTS_TABLE_FILTERS, FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_TABLE_COLUMN_SORT, FewShotDocumentActionTypes.SET_FEW_SHOT_DOCUMENTS_TABLE_FILTERS], tableConfigUpdated);
}

export function* attemptOpenDocument({ payload }: ReturnType<typeof openFewShotDocumentStarted>) {
    try {
        const { location, mlFewShotDocumentId, mimeType, shouldRedirect } = payload;
        const documentBlob: Blob = yield call(openFewShotDocument, { location });
        const { document, queries }: { document: MLFewShotDocument; queries: FewShotDocumentQuery[]; } = yield call(getFewShotDocumentById, { mlFewShotDocumentId });
        const documentUrl: string = yield call(getDocumentUrl, mimeType, documentBlob);
        yield put(openFewShotDocumentSuccessful(documentUrl, document, queries));
        if (shouldRedirect) {
            yield put(push(`/system/ai/few-shot/analysis/${document.agreementTypeId}/${mlFewShotDocumentId}`));
        }
    } catch (e) {
        yield put(openFewShotDocumentFailed((e as Error).message));
        toast.error('Unable to open document. Please try again.');
    }
}

function* openFewShotDocumentWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.OPEN_FEW_SHOT_DOCUMENT_STARTED, attemptOpenDocument);
}

export function* attemptOpenDocumentAndQueries({ payload }: ReturnType<typeof openFewShotDocumentAndQueries>) {
    try {
        const { mlFewShotDocumentId, agreementTypeId } = payload;
        const shouldRedirect = false;
        const { document }: { document: MLFewShotDocument; } = yield call(getFewShotDocumentById, { mlFewShotDocumentId });
        yield put(setSelectedAgreementTypeId(agreementTypeId, shouldRedirect));
        yield put(openFewShotDocumentStarted(document.location, mlFewShotDocumentId, document.mimeType, shouldRedirect));
    } catch (e) {
        yield put(push('/system/ai/few-shot'));
    }
}

function* openFewShotDocumentAndQueriesWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.OPEN_FEW_SHOT_DOCUMENT_AND_QUERIES, attemptOpenDocumentAndQueries);
}

export function* uploadFiles(files: File[], details: FewShotDocumentUploadInfo[]) {
    let request = new FormData();
    const numberOfFilesToUpload = files.reduce((acc, cur) => {
        const updatedTotalFileSize = acc.total + cur.size;
        if (updatedTotalFileSize < MAX_UPLOAD_SIZE) {
            return { total: updatedTotalFileSize, numberOfFiles: acc.numberOfFiles + 1 };
        }
        return acc;
    }, { total: 0, numberOfFiles: 0 }).numberOfFiles;
    const filesToUpload = files.slice(0, numberOfFilesToUpload);
    filesToUpload.forEach(file => request.append('files', file));
    const fileInfo = filesToUpload.map(file => details.find(({ documentDescription }) => documentDescription === file.name)!);
    request.append('fileInfo', JSON.stringify(fileInfo));
    yield call(uploadFewShotDocuments, request);
    files.splice(0, numberOfFilesToUpload);
    yield put(setInitialDocuments(files));
}

// 80MB
const MAX_UPLOAD_SIZE = 8e7;

export function* attemptUploadDocuments() {
    try {
        const allFiles: File[] = yield select(getFilesToUpload);
        const details: FewShotDocumentUploadInfo[] = yield select(getFileUploadInformation);
        let files = [...allFiles];
        const someFilesTooBig = files.some(file => file.size > MAX_UPLOAD_SIZE);
        if (someFilesTooBig) {
            toast.error('Some of the files you have attempted to upload are too large (over 80mb), please compress them and try again.');
            return;
        }
        while (files.length) {
            yield call(uploadFiles, files, details);
            const updatedAllFiles: File[] = yield select(getFilesToUpload);
            files = [...updatedAllFiles];
        }
        yield put(uploadFewShotDocumentsSuccessful());
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(uploadFewShotDocumentsFailed(errorMessage));
        toast.error('There was a problem uploading your document. Please try again.');
    }
}

function* uploadWatcher() {
    yield takeLatest(FewShotDocumentActionTypes.UPLOAD_FEW_SHOT_DOCUMENTS_STARTED, attemptUploadDocuments);
}

export function* checkMLAnswerHighlights() {
    try {
        const documentViewerType: DocumentViewerType | null = yield select(getDocumentViewerType);
        if (documentViewerType === DocumentViewerType.FEW_SHOT_DOCUMENT) {
            const schedulePageText: PdfPageText[] = yield select(getPdfText);
            const mlData: MLData | null = yield select(getFewShotAnswers);
            if (!isNull(mlData)) {
                const answersWithoutHighlightIndexes = Object.entries(mlData).map(([key, { highlightIndexes, page, snippet }]) => ({ key, highlightIndexes, page, snippet })).filter(({ highlightIndexes, page, snippet }) => ((isUndefined(highlightIndexes) || highlightIndexes.length === 0) && !!page && !!snippet));
                for (const fewShotMLData of answersWithoutHighlightIndexes) {
                    const { page, snippet, key } = fewShotMLData;
                    const pageText = schedulePageText.find(({ pageNumber }) => pageNumber === page);
                    if (!isUndefined(pageText) && !isUndefined(snippet)) {
                        const { uniqueIndexes, isFuzzyMatchHighlight } = getHighlightIndexesForPageText(snippet!, pageText);
                        yield put(setAIAnswerHighlightIndexes(key, uniqueIndexes, isFuzzyMatchHighlight));
                    }
                }
                yield put(setIsCheckingHighlightIndexes(false));
            }
        }
    } catch (e) {
        toast.error('We encountered a problem checking where the answers were found.');
    }
}

function* checkMLAnswerHighlightsWatcher() {
    yield debounce(1000, PdfActionTypes.SET_PDF_TEXT, checkMLAnswerHighlights);
}

export function* updateFewShotAnswers() {
    try {
        const currentFewShotDocument: MLFewShotDocument | null = yield select(getFewShotDocument);
        const savedFewShotDocument: MLFewShotDocument | null = yield select(getSavedFewShotDocument);
        const fewShotSelectedAgreementType: number = yield select(getSelectedAgreementTypeId);
        if (!isNull(currentFewShotDocument) && !isNull(savedFewShotDocument) && !isEqual(currentFewShotDocument, savedFewShotDocument) && !isNull(currentFewShotDocument.fewShotAnswers)) {
            const { fewShotAnswers, mlFewShotDocumentId } = currentFewShotDocument;
            yield call(updateFewShotDocumentAnswers, { fewShotAnswers, mlFewShotDocumentId });
            yield put(updateFewShotAnswersSuccessful());
            yield put(fetchFewShotDocumentsStarted());
            const priorityOrder: DocumentNameFewShotPriorityOrder = yield call(getFewShotDocumentPriorityOrder, { agreementTypeId: fewShotSelectedAgreementType });
            const setInitial = true;
            yield put(updateFewShotPriorityOrder(priorityOrder, setInitial));
            const navigationPath = `/system/ai/few-shot/${fewShotSelectedAgreementType}`;
            yield put(push(navigationPath));
        }
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(updateFewShotAnswersFailed(errorMessage));
        toast.error('Unable to update few shot answers. Please try again.');
    }
}

function* updateFewShotAnswersWatcher() {
    yield takeLeading(FewShotDocumentActionTypes.UPDATE_FEW_SHOT_ANSWERS_STARTED, updateFewShotAnswers);
}

export function* attemptDeleteFewShotDocument() {
    try {
        const documentToDelete: FewShotDocumentTable | null = yield select(getDocumentToDelete);
        if (!isNull(documentToDelete)) {
            yield call(deleteFewShotDocument, { mlFewShotDocumentId: documentToDelete.mlFewShotDocumentId });
            const pageNumber: number = yield select(getTablePageNumber);
            yield put(fetchFewShotDocumentsStarted(pageNumber));
        }
        yield put(deleteFewShotDocumentSuccessful());
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(deleteFewShotDocumentFailed(errorMessage));
        toast.error('Unable to delete few shot document. Please try again.');
    }
}

function* deleteFewShotDocumentWatcher() {
    yield takeLeading(FewShotDocumentActionTypes.DELETE_FEW_SHOT_DOCUMENT_STARTED, attemptDeleteFewShotDocument);
}

export function* attemptUpdatePriorityOrder() {
    try {
        const hasUpdated: boolean = yield select(getPriorityOrderHasUpdated);
        if (hasUpdated) {
            const currentPriorityOrder: DocumentNameFewShotPriorityOrder = yield select(getCurrentPriorityOrder);
            const order = flattenDeep(Object.values(currentPriorityOrder));
            const clientId: number | undefined = yield select(getSelectedClientId);
            const isSystem = isUndefined(clientId);
            yield call(updateFewShotDocumentPriorityOrder, { order, isSystem, clientId });
            const fewShotDocuments: FewShotDocumentTable[] = yield select(getFewShotDocuments);
            const total: number = yield select(getFewShotDocumentsTotal);
            const updatedPriorityFewShotDocuments = fewShotDocuments.map(fewShotDocument => {
                const updatedPriority = order.find(({ mlFewShotDocumentId }) => fewShotDocument.mlFewShotDocumentId === mlFewShotDocumentId)?.priority;
                if (!isUndefined(updatedPriority)) {
                    return set('priority', updatedPriority, fewShotDocument);
                }
                return fewShotDocument;
            });
            yield put(fetchFewShotDocumentsSuccessful(updatedPriorityFewShotDocuments, total));
        }
        yield put(updateFewShotPriorityOrderSuccessful());
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(updateFewShotPriorityOrderFailed(errorMessage));
        toast.error('Unable to save updated priority order. Please try again.');
    }
}

function* updatePriorityOrderWatcher() {
    yield takeLeading(FewShotDocumentActionTypes.UPDATE_FEW_SHOT_PRIORITY_ORDER_STARTED, attemptUpdatePriorityOrder);
}

export function* fewShotDocumentsSaga() {
    yield all([
        fork(selectedAgreementTypeUpdatedWatcher),
        fork(fetchFewShotDocumentsWatcher),
        fork(paginationNextWatcher),
        fork(paginationPreviousWatcher),
        fork(tableConfigUpdatedWatcher),
        fork(openFewShotDocumentWatcher),
        fork(uploadWatcher),
        fork(openFewShotDocumentAndQueriesWatcher),
        fork(checkMLAnswerHighlightsWatcher),
        fork(updateFewShotAnswersWatcher),
        fork(deleteFewShotDocumentWatcher),
        fork(updatePriorityOrderWatcher)
    ]);
}
